/**
 * SearchBar - Search input with debounce and filter dropdowns
 * 
 * Features:
 * - Debounced search input
 * - Filter dropdowns
 * - Clear button
 * - Loading state
 */

import React, { useState, useEffect, useRef } from 'react';

const SearchBar = ({
  placeholder = 'Search...',
  onSearch,
  filters = [],
  debounceMs = 300,
  loading = false,
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm, activeFilters);
      }
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, activeFilters, debounceMs, onSearch]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filterKey, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleClear = () => {
    setSearchTerm('');
    setActiveFilters({});
  };

  const hasActiveSearch = searchTerm || Object.keys(activeFilters).some(key => activeFilters[key]);

  return (
    <div className={`admin-search-bar ${className}`}>
      <div className="admin-search-bar__input-wrapper">
        <span className="admin-search-bar__icon">🔍</span>
        <input
          type="text"
          className="admin-search-bar__input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          disabled={loading}
        />
        {loading && (
          <span className="admin-search-bar__loading">
            <div className="admin-spinner admin-spinner--sm"></div>
          </span>
        )}
        {hasActiveSearch && !loading && (
          <button
            className="admin-search-bar__clear"
            onClick={handleClear}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {filters.length > 0 && (
        <div className="admin-search-bar__filters">
          {filters.map((filter) => (
            <div key={filter.key} className="admin-search-bar__filter">
              <label className="admin-search-bar__filter-label">
                {filter.label}
              </label>
              <select
                className="admin-search-bar__filter-select"
                value={activeFilters[filter.key] || ''}
                onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                disabled={loading}
              >
                <option value="">All</option>
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
